/**
 * The one way to write a note, wherever notes are written.
 *
 * Extracted so the lead's activity log and every `NotesField` share it. They had
 * drifted, and the drift was the defect sales reported: the PRO company log got
 * a resizable textarea that saves on ⌘↵, and the lead — the record they use most
 * — kept a single-line `<input>` where **Enter posted immediately**. So a note
 * with two sentences in it could not be written at all, and a stray Enter filed
 * half a thought permanently, because notes are append-only.
 *
 * Two forms for one act is how that happens. There is now one.
 *
 * ## Enter is a newline, ⌘↵ saves
 *
 * The other way round is tempting for a one-line box and wrong for this one:
 * these are notes about conversations and they run to paragraphs. A control that
 * discards the rest of your sentence when you reach for a new line is a control
 * you stop using — which is what "vanskelig å legge til" described.
 */
import { useState } from 'react'
import { NOTE_MAX } from './notes'

export function NoteComposer({
  onSave,
  placeholder = 'Add a note…',
  label = 'New note',
  rows = 2,
}: {
  onSave: (text: string) => Promise<void>
  placeholder?: string
  label?: string
  rows?: number
}) {
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    const trimmed = text.trim()
    if (!trimmed || saving) return
    setSaving(true)
    setError(null)
    try {
      await onSave(trimmed)
      setText('')
    } catch (err) {
      // Surfaced rather than swallowed. An append-only log that silently fails
      // to append leaves somebody believing they recorded something.
      setError(err instanceof Error ? err.message : 'Could not save the note.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="notes__add">
        <textarea
          className="input notes__input"
          rows={rows}
          value={text}
          maxLength={NOTE_MAX}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault()
              void save()
            }
          }}
        />
        <button
          className="btn btn--primary btn--sm"
          onClick={() => void save()}
          disabled={saving || !text.trim()}
          title="⌘↵ or Ctrl+↵"
        >
          {saving ? 'Saving…' : label}
        </button>
      </div>
      {error && <p className="recform__error">{error}</p>}
    </>
  )
}
